import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, Loader2, MessageSquareHeart } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

const EXPERIENCE = ["Excellent", "Very Good", "Good", "Fair", "Poor"];
const WELCOMED = ["Extremely welcomed", "Very welcomed", "Somewhat welcomed", "Not very welcomed", "Not welcomed"];
const POSITIVES = ["Welcome / hospitality", "Worship / music", "Message / preaching", "Prayer / spiritual atmosphere", "People / fellowship", "Children's / Youth ministry", "Facility / environment", "Organization / service flow", "Other"];
const LIKELY = ["Definitely will return", "Probably will return", "Not sure", "Probably will not return", "Definitely will not return"];
const ENCOURAGE = ["Sunday Worship", "Bible Study", "Prayer", "New Believers Class", "Men's / Women's Ministry", "Youth / Children's Ministry", "Community / outreach activities", "Special events / conferences", "Opportunities to meet and connect with people", "Pastoral connection", "Opportunities to serve / volunteer", "Other"];
const CONNECTION = ["Yes", "Somewhat", "No"];
const LOOKING = ["Yes, actively looking", "Possibly", "No, I already have a church home", "No, I'm just visiting", "Not sure"];
const FOLLOWUP = ["Yes", "Maybe", "No"];
const FOLLOWUP_BY = ["Pastor", "Ministry leader", "Someone I met today", "No preference"];
const RETURN_INFLUENCE = ["Preaching / message", "Worship", "Spiritual experience", "Personal invitation", "Friendliness / welcome", "Relationship with someone at LFCC", "Children's / Youth ministry", "Follow-up after my first visit", "Church beliefs / teaching", "Location / convenience", "Special event", "Other"];
const NOT_RETURNED_REASONS = ["Schedule / timing", "Distance / location", "Found another church", "Service style", "Teaching / theology", "Didn't feel connected", "Children's / youth needs", "Personal circumstances", "Only visiting the area", "Not currently interested in church", "Other", "Prefer not to say"];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function Pill({ selected, onClick, label, testid, disabled }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={`min-h-[44px] px-4 py-2 rounded-full border text-sm font-medium text-left transition-[background-color,border-color,color,transform] duration-200 active:scale-[0.97] disabled:opacity-35 ${
        selected ? "bg-river text-alabaster border-river shadow-md shadow-river/20" : "bg-white text-foreground border-border hover:border-water hover:text-river"
      }`}
    >
      <span className="inline-flex items-center gap-2">
        {selected && <Check className="w-3.5 h-3.5" />}
        {label}
      </span>
    </button>
  );
}

function Question({ n, title, hint, children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-border p-6" data-testid={testid}>
      <p className="text-xs font-mono text-ember mb-1.5">Question {n}</p>
      <h3 className="font-serif text-xl sm:text-2xl text-river mb-1">{title}</h3>
      {hint && <p className="text-sm text-muted-foreground mb-4">{hint}</p>}
      {!hint && <div className="mb-4" />}
      {children}
    </div>
  );
}

export default function Survey() {
  const [params] = useSearchParams();
  const isMissYou = params.get("kind") === "missyou";
  const [form, setForm] = useState({
    visit_type: isMissYou ? "not_returned" : "first",
    reference: params.get("ref") || "",
    overall_experience: "", welcomed: "", positives: [],
    uncomfortable: "", uncomfortable_detail: "", likely_return: "", encourage_return: [], encourage_other: "",
    connection: "", connection_who: "", looking_for_home: "", followup_wanted: "", followup_by: [],
    one_thing_better: "", return_influence: [], not_returned_reasons: [], prayer_request: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v, max) =>
    setForm((f) => {
      const cur = f[k];
      if (cur.includes(v)) return { ...f, [k]: cur.filter((x) => x !== v) };
      if (max && cur.length >= max) return f;
      return { ...f, [k]: [...cur, v] };
    });

  const valid =
    form.followup_wanted &&
    (form.visit_type === "not_returned"
      ? form.not_returned_reasons.length > 0
      : form.overall_experience && form.welcomed && form.uncomfortable && form.likely_return &&
        form.connection && form.looking_for_home &&
        (form.uncomfortable !== "Yes" || form.uncomfortable_detail.trim()) &&
        (form.visit_type !== "return" || form.return_influence.length > 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/survey", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  let q = 0;
  const next = () => ++q;

  return (
    <div className="min-h-screen bg-background texture-grain" data-testid="survey-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-alabaster/85 border-b border-border/60">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="survey-brand-link">
            <Logo size={38} />
            <span className="font-serif font-semibold text-river text-lg">LFCC</span>
          </a>
          <a href="/" data-testid="survey-back-link" className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-river px-3 py-2 rounded-full transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to LFCC
          </a>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        {done ? (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="bg-white rounded-3xl border border-border shadow-xl shadow-river/5 p-8 sm:p-12 text-center" data-testid="survey-success-card">
            <div className="mx-auto w-16 h-16 rounded-full bg-river flex items-center justify-center mb-6">
              <MessageSquareHeart className="w-7 h-7 text-ember-light" />
            </div>
            <p className="text-xs uppercase tracking-[0.25em] font-mono text-ember mb-3">Survey Received</p>
            <h3 className="font-serif text-3xl sm:text-4xl text-river mb-4">Thank you for sharing.</h3>
            <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
              Your honest feedback helps us serve every guest better. We're grateful you took the time.
            </p>
            <a href="/" data-testid="survey-home-button" className="inline-flex items-center gap-2 mt-8 min-h-[48px] px-7 rounded-full bg-river text-alabaster text-sm font-semibold hover:bg-river-light transition-colors">
              Back to LFCC
            </a>
          </motion.div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-10">
              <p className="text-xs uppercase tracking-[0.25em] font-mono text-ember mb-3">Visitor Experience Survey</p>
              <h1 className="font-serif text-4xl sm:text-5xl text-river">{form.visit_type === "not_returned" ? "We appreciated having you." : "How was your visit?"}</h1>
              <p className="text-muted-foreground mt-4 max-w-md mx-auto">
                {form.visit_type === "not_returned"
                  ? "We're always trying to serve people better. If you're comfortable sharing, a few quick answers would genuinely help — this is research, not pressure."
                  : "About 10 quick questions — mostly one tap each. Your answers are confidential and help us welcome people better."}
              </p>
            </motion.div>

            <div className="space-y-5">
              <Question n={next()} title="Was this your first visit to LFCC, or a return visit?" testid="survey-visit-type">
                <div className="flex flex-wrap gap-2.5">
                  <Pill testid="visit-type-first" label="My first visit" selected={form.visit_type === "first"} onClick={() => set("visit_type", "first")} />
                  <Pill testid="visit-type-return" label="A return visit" selected={form.visit_type === "return"} onClick={() => set("visit_type", "return")} />
                  <Pill testid="visit-type-not-returned" label="I haven't been back yet" selected={form.visit_type === "not_returned"} onClick={() => set("visit_type", "not_returned")} />
                </div>
              </Question>

              <Question n={next()} title="Link to your connection card (optional)" hint="If you filled out a Visitor Connection Card, enter your reference (e.g. LFCC-000123) or email — so you don't have to repeat your details.">
                <input data-testid="survey-reference-input" className="w-full min-h-[48px] rounded-xl border border-border bg-white px-4 text-base outline-none focus:border-water focus:ring-2 focus:ring-water/20" placeholder="LFCC-000123 or your email" value={form.reference} onChange={(e) => set("reference", e.target.value)} />
              </Question>

              <AnimatePresence>
                {form.visit_type === "return" && (
                  <motion.div key="return-q" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ type: "spring", stiffness: 200, damping: 26 }} className="overflow-hidden">
                    <Question n={next()} title="What influenced your decision to come back to LFCC?" hint="Choose all that apply." testid="survey-return-influence">
                      <div className="flex flex-wrap gap-2.5">
                        {RETURN_INFLUENCE.map((r) => (
                          <Pill key={r} testid={`influence-${slug(r)}`} label={r} selected={form.return_influence.includes(r)} onClick={() => toggle("return_influence", r)} />
                        ))}
                      </div>
                    </Question>
                  </motion.div>
                )}
                {form.visit_type === "not_returned" && (
                  <motion.div key="nr-q" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ type: "spring", stiffness: 200, damping: 26 }} className="overflow-hidden">
                    <Question n={next()} title="Is there anything that influenced your decision not to return yet?" hint="Choose all that apply — every answer helps us serve better." testid="survey-not-returned">
                      <div className="flex flex-wrap gap-2.5">
                        {NOT_RETURNED_REASONS.map((r) => (
                          <Pill key={r} testid={`not-returned-${slug(r)}`} label={r} selected={form.not_returned_reasons.includes(r)} onClick={() => toggle("not_returned_reasons", r)} />
                        ))}
                      </div>
                    </Question>
                  </motion.div>
                )}
              </AnimatePresence>

              {form.visit_type !== "not_returned" && (
                <>
                  <Question n={next()} title="Overall, how would you describe your experience at LFCC?" testid="survey-overall">
                    <div className="flex flex-wrap gap-2.5">
                      {EXPERIENCE.map((o) => <Pill key={o} testid={`overall-${slug(o)}`} label={o} selected={form.overall_experience === o} onClick={() => set("overall_experience", o)} />)}
                    </div>
                  </Question>

                  <Question n={next()} title="How welcomed and comfortable did you feel during your visit?" testid="survey-welcomed">
                    <div className="flex flex-wrap gap-2.5">
                      {WELCOMED.map((o) => <Pill key={o} testid={`welcomed-${slug(o)}`} label={o} selected={form.welcomed === o} onClick={() => set("welcomed", o)} />)}
                    </div>
                  </Question>

                  <Question n={next()} title="What stood out most positively about your experience?" hint="Select up to 3." testid="survey-positives">
                    <div className="flex flex-wrap gap-2.5">
                      {POSITIVES.map((o) => <Pill key={o} testid={`positive-${slug(o)}`} label={o} selected={form.positives.includes(o)} disabled={!form.positives.includes(o) && form.positives.length >= 3} onClick={() => toggle("positives", o, 3)} />)}
                    </div>
                  </Question>

                  <Question n={next()} title="Was there anything that made you uncomfortable, confused, or less likely to return?" testid="survey-uncomfortable">
                    <div className="flex gap-2.5 mb-3">
                      {["No", "Yes"].map((o) => <Pill key={o} testid={`uncomfortable-${o.toLowerCase()}`} label={o} selected={form.uncomfortable === o} onClick={() => set("uncomfortable", o)} />)}
                    </div>
                    <AnimatePresence>
                      {form.uncomfortable === "Yes" && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                          <textarea data-testid="uncomfortable-detail" rows={3} className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-water focus:ring-2 focus:ring-water/20 resize-none" placeholder="Please tell us — this helps us remove barriers." value={form.uncomfortable_detail} onChange={(e) => set("uncomfortable_detail", e.target.value)} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Question>

                  <Question n={next()} title="How likely are you to attend LFCC again?" testid="survey-likely">
                    <div className="flex flex-wrap gap-2.5">
                      {LIKELY.map((o) => <Pill key={o} testid={`likely-${slug(o)}`} label={o} selected={form.likely_return === o} onClick={() => set("likely_return", o)} />)}
                    </div>
                  </Question>

                  <Question n={next()} title="What would most encourage you to return?" hint="Choose up to 3." testid="survey-encourage">
                    <div className="flex flex-wrap gap-2.5">
                      {ENCOURAGE.map((o) => <Pill key={o} testid={`encourage-${slug(o)}`} label={o} selected={form.encourage_return.includes(o)} disabled={!form.encourage_return.includes(o) && form.encourage_return.length >= 3} onClick={() => toggle("encourage_return", o, 3)} />)}
                    </div>
                  </Question>

                  <Question n={next()} title="Did you make a meaningful connection with anyone at LFCC?" testid="survey-connection">
                    <div className="flex gap-2.5 mb-3">
                      {CONNECTION.map((o) => <Pill key={o} testid={`connection-${o.toLowerCase()}`} label={o} selected={form.connection === o} onClick={() => set("connection", o)} />)}
                    </div>
                    <AnimatePresence>
                      {form.connection === "Yes" && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                          <input data-testid="connection-who-input" className="w-full min-h-[48px] rounded-xl border border-border bg-white px-4 text-sm outline-none focus:border-water focus:ring-2 focus:ring-water/20" placeholder="Who did you connect with? (optional)" value={form.connection_who} onChange={(e) => set("connection_who", e.target.value)} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Question>

                  <Question n={next()} title="Are you currently looking for a church home?" testid="survey-looking">
                    <div className="flex flex-wrap gap-2.5">
                      {LOOKING.map((o) => <Pill key={o} testid={`looking-${slug(o)}`} label={o} selected={form.looking_for_home === o} onClick={() => set("looking_for_home", o)} />)}
                    </div>
                  </Question>
                </>
              )}

              <Question n={next()} title="Would you like someone from LFCC to personally follow up with you?" testid="survey-followup">
                <div className="flex gap-2.5 mb-3">
                  {FOLLOWUP.map((o) => <Pill key={o} testid={`followup-${o.toLowerCase()}`} label={o} selected={form.followup_wanted === o} onClick={() => set("followup_wanted", o)} />)}
                </div>
                <AnimatePresence>
                  {(form.followup_wanted === "Yes" || form.followup_wanted === "Maybe") && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                      <p className="text-sm font-semibold text-river mb-2.5">Who would you prefer to hear from?</p>
                      <div className="flex flex-wrap gap-2.5">
                        {FOLLOWUP_BY.map((o) => <Pill key={o} testid={`followup-by-${slug(o)}`} label={o} selected={form.followup_by.includes(o)} onClick={() => toggle("followup_by", o)} />)}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Question>

              <Question n={next()} title="Is there anything we can pray for?" hint="Optional — our prayer team would be honoured to stand with you. Shared only with pastoral care." testid="survey-prayer">
                <textarea data-testid="survey-prayer-input" rows={3} className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-water focus:ring-2 focus:ring-water/20 resize-none" placeholder="Your request is kept confidential." value={form.prayer_request} onChange={(e) => set("prayer_request", e.target.value)} />
              </Question>

              <Question n={next()} title="If you could tell Pastor and the LFCC leadership ONE thing that would make the visitor experience better, what would it be?" hint="Optional — but often the most valuable answer." testid="survey-one-thing">
                <textarea data-testid="one-thing-input" rows={4} className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-water focus:ring-2 focus:ring-water/20 resize-none" placeholder="Share honestly — we're listening." value={form.one_thing_better} onChange={(e) => set("one_thing_better", e.target.value)} />
              </Question>

              <button
                data-testid="survey-submit-button"
                onClick={submit}
                disabled={!valid || submitting}
                className="w-full min-h-[52px] rounded-full bg-ember text-white font-semibold hover:bg-ember-light disabled:opacity-40 transition-[background-color,transform] active:scale-[0.98] inline-flex items-center justify-center gap-2 shadow-lg shadow-ember/25"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Submit My Feedback
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
